"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";

// Shared by Navbar (its pending-scroll effect runs on every page via the
// layout) — a cross-page hash click stores its target here and the Navbar
// effect performs the scroll after the home sections mount.
export const PENDING_SCROLL_KEY = "navbar.pendingScrollTo";

// NEVER scrollIntoView here: it also scrolls every scrollable ancestor, and
// overflow-hidden sections (e.g. the hero) are scrollable by JS but not by
// the user — they end up permanently clipped (hero stuck 120px up, wheel
// can't undo it). Scroll ONLY the window, and zero out any ancestor offsets
// a native fragment jump may have left behind.
export function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (!el) return false;
  for (let n = el.parentElement; n; n = n.parentElement) {
    if (n !== document.scrollingElement && n.scrollTop) n.scrollTop = 0;
  }
  const offset = id === "download" ? window.innerHeight * 0.35 : 80;
  const top = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  return true;
}

/**
 * Link that intercepts "/#anchor" hrefs with the same JS-scroll treatment
 * the Navbar uses — the hash never lands in the URL, so the browser can't
 * re-anchor and strand the page mid-scroll (the "/#download can't scroll
 * back to top" bug, which the plain footer links reintroduced: a same-page
 * hash click doesn't change pathname, so the Navbar's repair effect never
 * fires). Non-hash hrefs behave exactly like next/link.
 */
export function HashLink({
  href,
  className,
  children,
  onNavigate,
}: {
  href: string;
  className?: string;
  children: React.ReactNode;
  onNavigate?: () => void;
}) {
  const pathname = usePathname();
  const router = useRouter();

  return (
    <Link
      href={href}
      className={className}
      onClick={(e) => {
        if (!href.startsWith("/#")) {
          onNavigate?.();
          return;
        }
        e.preventDefault();
        onNavigate?.();
        const id = href.slice(2);
        if (pathname === "/") {
          scrollToId(id);
        } else {
          sessionStorage.setItem(PENDING_SCROLL_KEY, id);
          router.push("/");
        }
      }}
    >
      {children}
    </Link>
  );
}
